/**
 * ChatMessage — renders a single message in the AI chat.
 *
 * Design: Inspired by ChatGPT/Claude — clean, minimal, no loud bubbles.
 * - User messages: subtle rounded pill, soft muted background
 * - AI messages: no background, clean text with good typography
 * - Tool calls: collapsed subtle indicators, not prominent
 */

import { memo, useState, useCallback, useMemo } from "react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { cn } from "@/shared/utils/general/cn"
import { useRevealTask } from "@/features/search/useRevealTask"
import type { ChatMessage as ChatMessageType } from "./useAiChat"
import CheckIcon from "@/shared/icons/totodoicons/stroke/checkmark.svg?react"
import { CircularProgress } from "@/app/components/ui/CircularProgress"
import AlertIcon from "@/shared/icons/pikaicons/stroke/alerts/alert-triangle.svg?react"
import ThumbUpIcon from "@/shared/icons/pikaicons/stroke/general/thumb-reaction-like.svg?react"
import ThumbDownIcon from "@/shared/icons/pikaicons/stroke/general/thumb-reaction-dislike.svg?react"
import CopyIcon from "@/shared/icons/pikaicons/stroke/general/copy-default.svg?react"
import CopiedIcon from "@/shared/icons/pikaicons/stroke/general/copy-copied.svg?react"
import ChevronDownIcon from "@/shared/icons/totodoicons/stroke/chevron-down.svg?react"
import GoToIcon from "@/shared/icons/pikaicons/stroke/arrows-chevrons/external-link-square.svg?react"
import PinIcon from "@/shared/icons/pikaicons/stroke/general/pin-default.svg?react"
import { DndContext } from "@dnd-kit/core"
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { ChecklistItem } from "@/features/task-detail/components/checklist/ChecklistItem"
import { TaskItem } from "@/features/tasks/components/TaskItem"
import { completeTask, uncompleteTask, deleteChecklistItem } from "@/db/operations"
import { db, ObjectType } from "@/db/schema"
import { useLiveQueryImmediate } from "@/db/hooks"
import type { DbObject } from "@/db/schema"

// Only render rich results for these (final user-facing results, not intermediate lookups)
const CHECKLIST_TOOLS = new Set(["getTaskChecklist"])
const TASK_VIEW_TOOLS = new Set([
  "getTodayTasks",
  "getUpcomingTasks",
  "getInboxTasks",
  "getAnydayTasks",
  "getSomedayTasks",
  "getEveningTasks",
  "getLogbook",
  "getTrash",
  "listTasks",
  "getTasksWithDeadline",
])
const SEARCH_TOOLS = new Set(["searchTasks"])
const CONTAINER_TOOLS = new Set(["searchContainers", "listProjects", "getContainers"])
const TAG_TOOLS = new Set(["listTags"])

// Action tools that indicate searchTasks was used as a lookup (suppress search result rendering)
const ACTION_TOOLS = new Set([
  "moveTask", "moveMultipleTasks", "scheduleTask", "bulkSchedule",
  "completeTask", "bulkComplete", "deleteTask", "bulkDelete",
  "updateTask", "cancelTask", "duplicateTask", "restoreTask",
  "clearSchedule", "bulkUncomplete", "bulkCancel", "bulkRestore",
  "bulkUpdateDueDate",
])

// All tools that get rich UI rendering — their tool chips should be hidden
const RICH_RENDERED_TOOLS = new Set([
  ...CHECKLIST_TOOLS,
  ...TASK_VIEW_TOOLS,
  ...SEARCH_TOOLS,
  ...CONTAINER_TOOLS,
  ...TAG_TOOLS,
  "askUserChoice",
  "showForm",
  "showConfirm",
  "showTaskPreview",
  "showDatePicker",
])

interface ChatMessageProps {
  message: ChatMessageType
  onFeedback?: (messageId: string, feedback: "up" | "down" | undefined) => void
  onTogglePin?: (messageId: string) => void
  onSendMessage?: (text: string) => void
}

export const ChatMessageBubble = memo(function ChatMessageBubble({
  message,
  onFeedback,
  onTogglePin,
  onSendMessage,
}: ChatMessageProps) {
  const isUser = message.role === "user"
  const toolCalls = message.toolCalls ?? []
  const [toolsExpanded, setToolsExpanded] = useState(() => {
    // Auto-expand if all tools failed so user can see what went wrong
    if (!toolCalls.length) return false
    const allComplete = toolCalls.every((tc) => message.toolResults?.find((tr) => tr.name === tc.name))
    if (!allComplete) return false
    const allSucceeded = toolCalls.every((tc) => {
      const result = message.toolResults?.find((tr) => tr.name === tc.name)
      if (!result) return false
      const r = result.result as Record<string, unknown> | null | undefined
      return r && typeof r === "object" && !("error" in r)
    })
    return !allSucceeded
  })

  const hasToolCalls = toolCalls.length > 0
  const allToolsComplete = hasToolCalls && toolCalls.every((tc) => {
    const result = message.toolResults?.find((tr) => tr.name === tc.name)
    return !!result
  })
  const allToolsSucceeded = allToolsComplete && toolCalls.every((tc) => {
    const result = message.toolResults?.find((tr) => tr.name === tc.name)
    if (!result) return false
    const r = result.result as Record<string, unknown> | null | undefined
    return r && typeof r === "object" && !("error" in r)
  })
  const anyToolFailed = allToolsComplete && !allToolsSucceeded

  if (isUser) {
    // Strip [uuid] patterns from display (used internally for choice responses)
    const displayContent = message.content.replace(/\s*\[[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\]/gi, "")
    return (
      <div className="flex w-full justify-end" data-role="user">
        <div className="max-w-[80%] rounded-2xl rounded-br-md bg-foreground/[0.06] px-3.5 py-2 text-sm leading-relaxed text-foreground dark:bg-foreground/[0.08]">
          <div className="whitespace-pre-wrap break-words select-text">{displayContent}</div>
        </div>
      </div>
    )
  }

  // Assistant message
  // Check if rich tool results will render (checklist/tasks/search) — if so, strip redundant text
  const hasRichResults = message.toolResults?.some((tr) => {
    const res = tr.result as { success?: boolean; data?: unknown } | undefined
    return res?.success && res.data && (CHECKLIST_TOOLS.has(tr.name) || TASK_VIEW_TOOLS.has(tr.name) || SEARCH_TOOLS.has(tr.name) || CONTAINER_TOOLS.has(tr.name))
  })
  const displayedContent = useMemo(() => {
    if (!message.content || !hasRichResults) {
      // Even without rich results, strip leaked [context: ...] metadata
      if (message.content?.includes("[context:")) {
        return message.content.replace(/\[context:.*?\]/gs, "").trim()
      }
      return message.content
    }
    // Strip markdown bullet lists and lines containing task IDs (already rendered as interactive list)
    return message.content
      .replace(/\[context:.*?\]/gs, "")
      .replace(/^[*\-]\s+.+$/gm, "")
      .replace(/^.*\(ID:\s*[0-9a-f-]{36}\).*$/gim, "")
      // Strip "Dessa visas ovanför/nedan/här" type phrases the AI sometimes adds
      .replace(/\.?\s*(?:Dessa|De|Resultaten|Uppgifterna)\s+visas\s+(?:ovanför|nedan|här|under|ovan)\.?/gi, ".")
      .replace(/\n{3,}/g, "\n\n")
      .trim()
  }, [message.content, hasRichResults])

  return (
    <div className="flex w-full justify-start">
      <div className="max-w-[90%] space-y-1.5">
        {/* Thinking indicator — show until actual visible text or tool calls appear */}
        {message.isStreaming && (!message.content || message.content.trim().length === 0) && !hasToolCalls && (
          <div className="flex items-center gap-2 py-1">
            <div className="flex gap-1">
              <span className="size-1.5 animate-bounce rounded-full bg-muted-foreground/50 [animation-delay:0ms]" />
              <span className="size-1.5 animate-bounce rounded-full bg-muted-foreground/50 [animation-delay:150ms]" />
              <span className="size-1.5 animate-bounce rounded-full bg-muted-foreground/50 [animation-delay:300ms]" />
            </div>
          </div>
        )}

        {/* Message content — markdown rendered */}
        {displayedContent && (
          <div className="text-sm leading-relaxed text-foreground select-text prose prose-sm prose-neutral dark:prose-invert prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-headings:my-2 prose-headings:text-foreground prose-a:text-primary max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {displayedContent}
            </ReactMarkdown>
            {/* Streaming cursor */}
            {message.isStreaming && (
              <span className="ml-0.5 inline-block h-4 w-[2px] animate-pulse bg-foreground/60 align-text-bottom" />
            )}
          </div>
        )}

        {/* Rich tool results — inline checklist/task components */}
        <RichToolResults toolResults={message.toolResults} toolCalls={message.toolCalls} onSendMessage={onSendMessage} />

        {/* Tool calls — subtle collapsed section (hidden when all tools are rendered as rich results) */}
        {hasToolCalls && !toolCalls.every((tc) => RICH_RENDERED_TOOLS.has(tc.name)) && (
          <div className="pt-0.5">
            {(() => {
              // Filter out tool calls that are already rendered as rich results
              const visibleToolCalls = toolCalls.filter((tc) => !RICH_RENDERED_TOOLS.has(tc.name))
              if (!visibleToolCalls.length) return null
              if (visibleToolCalls.length === 1 && visibleToolCalls[0]) {
                return (
                  <ToolChip
                    toolCall={visibleToolCalls[0]}
                    result={message.toolResults?.find((tr) => tr.name === visibleToolCalls[0]!.name)}
                  />
                )
              }
              return (
                <div>
                  <button
                    onClick={() => setToolsExpanded(!toolsExpanded)}
                    className="flex items-center gap-1.5 text-[11px] text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {allToolsComplete ? (
                      anyToolFailed ? (
                        <AlertIcon className="size-3 text-destructive" />
                      ) : (
                        <CheckIcon className="size-3 text-emerald-600 dark:text-emerald-400" />
                      )
                    ) : (
                      <CircularProgress className="size-3" />
                    )}
                    <span>
                      {allToolsComplete
                        ? anyToolFailed
                          ? `${visibleToolCalls.length} actions failed — tap to see details`
                          : `${visibleToolCalls.length} actions completed`
                        : `Running ${visibleToolCalls.length} actions...`}
                    </span>
                    <svg
                      className={cn("size-3 transition-transform", toolsExpanded && "rotate-180")}
                      viewBox="0 0 12 12"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    >
                      <path d="M3 4.5L6 7.5L9 4.5" />
                    </svg>
                  </button>
                  {toolsExpanded && (
                    <div className="mt-1.5 space-y-1 pl-4 border-l border-border/50">
                      {visibleToolCalls.map((tc, i) => (
                        <ToolChip
                          key={tc.id || i}
                          toolCall={tc}
                          result={message.toolResults?.find((tr) => tr.name === tc.name)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )
            })()}
          </div>
        )}

        {/* Action buttons — show on non-streaming assistant messages */}
        {!message.isStreaming && message.content && (
          <div className="flex items-center gap-0.5 pt-0.5">
            <CopyButton text={message.content} />
            {onTogglePin && (
              <button
                onClick={() => onTogglePin(message.id)}
                className={cn(
                  "flex size-6 items-center justify-center rounded-md transition-colors",
                  message.isPinned
                    ? "text-amber-500"
                    : "text-muted-foreground/40 hover:text-muted-foreground",
                )}
                title={message.isPinned ? "Unpin message" : "Pin message to preserve context"}
              >
                <PinIcon className="size-3" />
              </button>
            )}
            {onFeedback && (
              <>
                <button
                  onClick={() => onFeedback(message.id, message.feedback === "up" ? undefined : "up")}
                  className={cn(
                    "flex size-6 items-center justify-center rounded-md transition-colors",
                    message.feedback === "up"
                      ? "text-emerald-600 dark:text-emerald-400"
                      : "text-muted-foreground/40 hover:text-muted-foreground",
                  )}
                  title="Good response"
                >
                  <ThumbUpIcon className="size-3" />
                </button>
                <button
                  onClick={() => onFeedback(message.id, message.feedback === "down" ? undefined : "down")}
                  className={cn(
                    "flex size-6 items-center justify-center rounded-md transition-colors",
                    message.feedback === "down"
                      ? "text-destructive"
                      : "text-muted-foreground/40 hover:text-muted-foreground",
                  )}
                  title="Bad response"
                >
                  <ThumbDownIcon className="size-3" />
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
})

// ---------------------------------------------------------------------------
// ToolChip — single tool call indicator
// ---------------------------------------------------------------------------

interface ToolChipProps {
  toolCall: { id?: string; name: string; arguments?: Record<string, unknown> }
  result?: { result: unknown }
}

function ToolChip({ toolCall, result }: ToolChipProps) {
  const hasError =
    result &&
    (
      // Object result with error field: { success: false, error: "..." }
      (typeof result.result === "object" &&
        result.result !== null &&
        "error" in (result.result as Record<string, unknown>)) ||
      // Object result with success: false
      (typeof result.result === "object" &&
        result.result !== null &&
        (result.result as Record<string, unknown>).success === false) ||
      // String result starting with "Error:" (timeout/exception)
      (typeof result.result === "string" &&
        (result.result as string).startsWith("Error:"))
    )
  const isComplete = !!result && !hasError
  const isFailed = !!hasError

  return (
    <div
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[11px] font-medium",
        isComplete && "text-emerald-700 dark:text-emerald-400",
        isFailed && "text-destructive",
        !result && "text-muted-foreground",
      )}
    >
      {isComplete ? (
        <CheckIcon className="size-2.5 shrink-0" />
      ) : isFailed ? (
        <AlertIcon className="size-2.5 shrink-0" />
      ) : (
        <CircularProgress className="size-2.5 shrink-0" />
      )}
      <span>{formatToolName(toolCall.name)}</span>
      {isComplete && toolCall.arguments && "title" in toolCall.arguments && (
        <span className="font-normal opacity-60">— {String(toolCall.arguments.title)}</span>
      )}
      {isFailed && (
        <span className="font-normal opacity-60">— {extractErrorReason(result)}</span>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatToolName(name: string): string {
  const actionMap: Record<string, string> = {
    createTask: "Created task",
    completeTask: "Completed task",
    uncompleteTask: "Reopened task",
    deleteTask: "Deleted task",
    updateTask: "Updated task",
    scheduleTask: "Scheduled task",
    moveTask: "Moved task",
    createProject: "Created project",
    createList: "Created list",
    createProjectWithTasks: "Created project with tasks",
    searchTasks: "Searched tasks",
    searchContainers: "Found containers",
    getTodayTasks: "Fetched today",
    getUpcomingTasks: "Fetched upcoming",
    getInboxTasks: "Fetched inbox",
  }
  return actionMap[name] ?? name
}

function extractErrorReason(result: { result: unknown } | undefined): string {
  if (!result) return "failed"
  const r = result.result
  if (typeof r === "string") {
    // Strip "Error:" prefix and trim
    const cleaned = r.replace(/^Error:\s*/i, "").trim()
    return cleaned.length > 60 ? cleaned.slice(0, 57) + "…" : cleaned || "failed"
  }
  if (typeof r === "object" && r !== null) {
    const obj = r as Record<string, unknown>
    if (typeof obj.error === "string") {
      const e = obj.error
      return e.length > 60 ? e.slice(0, 57) + "…" : e
    }
    if (typeof obj.message === "string") {
      const m = obj.message
      return m.length > 60 ? m.slice(0, 57) + "…" : m
    }
  }
  return "failed"
}

// ---------------------------------------------------------------------------
// CopyButton — copies message text to clipboard with visual feedback
// ---------------------------------------------------------------------------

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch { /* clipboard not available */ }
  }, [text])

  return (
    <button
      onClick={handleCopy}
      className="flex size-6 items-center justify-center rounded-md text-muted-foreground/40 transition-colors hover:text-muted-foreground"
      title="Copy"
    >
      {copied ? (
        <CopiedIcon className="size-3 text-emerald-600 dark:text-emerald-400" />
      ) : (
        <CopyIcon className="size-3" />
      )}
    </button>
  )
}

// ---------------------------------------------------------------------------
// RichToolResults — renders checklist items and tasks using real components
// ---------------------------------------------------------------------------

interface ToolResultData {
  success?: boolean
  data?: unknown
  _renderData?: unknown
}

interface ChoiceOption {
  label: string
  value: string
  description?: string
}

interface ChoiceData {
  question: string
  options: ChoiceOption[]
  type: "single" | "multiple"
  _uiAction: "choice"
}

interface FormFieldData {
  key: string
  label: string
  type: "text" | "date" | "number" | "select" | "toggle"
  required?: boolean
  placeholder?: string
  defaultValue?: string
  options?: string[]
}

interface FormData {
  title: string
  fields: FormFieldData[]
  submitLabel?: string
  _uiAction: "form"
}

interface ConfirmData {
  title: string
  message: string
  details?: string[]
  confirmLabel?: string
  cancelLabel?: string
  variant?: "default" | "destructive"
  _uiAction: "confirm"
}

interface TaskPreviewData {
  project: string
  tasks: Array<{ title: string; checklist?: string[] }>
  _uiAction: "taskPreview"
}

interface DatePickerData {
  label: string
  mode: "date" | "datetime" | "dateRange"
  suggestions?: string[]
  _uiAction: "datePicker"
}

function isChoiceData(data: unknown): data is ChoiceData {
  return !!data && typeof data === "object" && (data as ChoiceData)._uiAction === "choice"
}

function isFormData(data: unknown): data is FormData {
  return !!data && typeof data === "object" && (data as FormData)._uiAction === "form"
}

function isConfirmData(data: unknown): data is ConfirmData {
  return !!data && typeof data === "object" && (data as ConfirmData)._uiAction === "confirm"
}

function isTaskPreviewData(data: unknown): data is TaskPreviewData {
  return !!data && typeof data === "object" && (data as TaskPreviewData)._uiAction === "taskPreview"
}

function isDatePickerData(data: unknown): data is DatePickerData {
  return !!data && typeof data === "object" && (data as DatePickerData)._uiAction === "datePicker"
}

function RichToolResults({
  toolResults,
  toolCalls: toolCallsProp,
  onSendMessage,
}: {
  toolResults?: Array<{ toolCallId: string; name: string; result: unknown }>
  toolCalls?: Array<{ id: string; name: string; arguments: Record<string, unknown> }>
  onSendMessage?: (text: string) => void
}) {
  if (!toolResults?.length) return null

  // Only render the LAST askUserChoice (if AI called it multiple times, only the final one matters)
  let lastChoiceIdx = -1
  for (let i = toolResults.length - 1; i >= 0; i--) {
    const r = toolResults[i]!.result as ToolResultData | undefined
    if (toolResults[i]!.name === "askUserChoice" && isChoiceData(r?.data)) {
      lastChoiceIdx = i
      break
    }
  }
  // If askUserChoice is present, skip rendering searchTasks as collapsible (choice replaces it)
  const hasChoice = lastChoiceIdx >= 0

  // If action tools are present (in results OR pending calls), search was a lookup — suppress rendering
  const hasActionTool = toolResults.some((tr) => ACTION_TOOLS.has(tr.name)) ||
    (toolCallsProp?.some((tc) => ACTION_TOOLS.has(tc.name)) ?? false)

  // Merge all search results into one deduplicated list
  const mergedSearchTasks = useMemo(() => {
    if (hasChoice || hasActionTool) return []
    const seen = new Set<string>()
    const merged: DbObject[] = []
    for (const tr of toolResults) {
      if (!SEARCH_TOOLS.has(tr.name)) continue
      const res = tr.result as ToolResultData | undefined
      if (!res?.success || !res.data) continue
      const tasks = (res._renderData ?? res.data) as DbObject[]
      for (const task of tasks) {
        const id = task.id as string
        if (!seen.has(id)) {
          seen.add(id)
          merged.push(task)
        }
      }
    }
    return merged
  }, [toolResults, hasChoice, hasActionTool])

  // Deduplicate: only render the LAST instance of tools that should only appear once
  const lastPreviewIdx = useMemo(() => {
    for (let i = toolResults.length - 1; i >= 0; i--) {
      if (toolResults[i]!.name === "showTaskPreview") return i
    }
    return -1
  }, [toolResults])

  const lastContainerIdx = useMemo(() => {
    for (let i = toolResults.length - 1; i >= 0; i--) {
      if (CONTAINER_TOOLS.has(toolResults[i]!.name)) return i
    }
    return -1
  }, [toolResults])

  const lastTaskViewIdx = useMemo(() => {
    for (let i = toolResults.length - 1; i >= 0; i--) {
      if (TASK_VIEW_TOOLS.has(toolResults[i]!.name)) return i
    }
    return -1
  }, [toolResults])

  return (
    <>
      {toolResults.map((tr, idx) => {
        const res = tr.result as ToolResultData | undefined
        if (!res?.success || !res.data) return null

        // askUserChoice → only render the last one
        if (tr.name === "askUserChoice" && isChoiceData(res.data)) {
          if (idx !== lastChoiceIdx) return null
          return <InlineChoiceUI key={tr.toolCallId} choice={res.data} onSelect={onSendMessage} />
        }

        // showForm → render interactive form
        if (tr.name === "showForm" && isFormData(res.data)) {
          return <InlineFormUI key={tr.toolCallId} form={res.data} onSubmit={onSendMessage} />
        }

        // showConfirm → render confirmation dialog
        if (tr.name === "showConfirm" && isConfirmData(res.data)) {
          return <InlineConfirmUI key={tr.toolCallId} confirm={res.data} onSubmit={onSendMessage} />
        }

        // showTaskPreview → render task preview (only the last one)
        if (tr.name === "showTaskPreview" && isTaskPreviewData(res.data)) {
          if (idx !== lastPreviewIdx) return null
          return <InlineTaskPreviewUI key={tr.toolCallId} preview={res.data} onSubmit={onSendMessage} />
        }

        // showDatePicker → render date picker
        if (tr.name === "showDatePicker" && isDatePickerData(res.data)) {
          return <InlineDatePickerUI key={tr.toolCallId} picker={res.data} onSubmit={onSendMessage} />
        }

        if (CHECKLIST_TOOLS.has(tr.name)) {
          const items = res.data as Array<{ id: string; title: string; completed: boolean; canceled: boolean }>
          // Only track items that were active when the AI responded.
          // Completed/canceled items from the snapshot are excluded.
          // But if you complete one in the chat, it stays (live query keeps it).
          const activeItems = items.filter((i) => !i.completed && !i.canceled)
          if (!activeItems.length) return null
          return <InlineChecklist key={tr.toolCallId} items={activeItems} />
        }

        if (TASK_VIEW_TOOLS.has(tr.name)) {
          if (idx !== lastTaskViewIdx) return null
          const tasks = (res._renderData ?? res.data) as DbObject[]
          if (!tasks.length) return null
          return <InlineTaskList key={tr.toolCallId} tasks={tasks} />
        }

        // Search results rendered as merged list (skip individual rendering)
        if (SEARCH_TOOLS.has(tr.name)) return null

        // Container results (projects/lists/areas) rendered via TaskItem (same as tasks)
        // Suppress when action tools are present (container was looked up for move/etc.)
        if (CONTAINER_TOOLS.has(tr.name)) {
          if (hasActionTool && tr.name === "searchContainers") return null
          if (idx !== lastContainerIdx) return null
          const containers = (res._renderData ?? res.data) as DbObject[]
          if (!containers.length) return null
          return <InlineContainerList key={tr.toolCallId} containers={containers} />
        }

        // Tag list rendered as chips
        if (TAG_TOOLS.has(tr.name)) {
          const tags = (res.data as { tags?: Array<{ name: string; count: number }> })?.tags
          if (!tags?.length) return null
          return <InlineTagList key={tr.toolCallId} tags={tags} />
        }

        return null
      })}
      {/* Merged search results — single deduplicated list */}
      {mergedSearchTasks.length > 0 && (
        <CollapsibleTaskList tasks={mergedSearchTasks} count={mergedSearchTasks.length} />
      )}
    </>
  )
}

function InlineChecklist({ items }: { items: Array<{ id: string }> }) {
  const initialIds = useMemo(() => items.map((i) => i.id), [items])
  const handleDelete = useCallback((id: string) => { deleteChecklistItem(id) }, [])

  // Live query: fetch current state of all items, only hide deleted ones
  // Completed/canceled items stay visible (shown with checkmark styling)
  const [liveItems] = useLiveQueryImmediate(
    () => db.totodo.bulkGet(initialIds).then((rows) =>
      rows.filter((r): r is DbObject => r != null && !r.isDeleted)
    ),
    ["chat-checklist", ...initialIds],
    undefined,
  )

  const ids = useMemo(() => (liveItems ?? []).map((i) => i.id as string), [liveItems])

  if (!ids.length) return null

  return (
    <div className="mt-2 rounded-lg border border-border/40 bg-background/50 overflow-hidden" style={{ zoom: 0.85 }}>
      <DndContext>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          {ids.map((id) => (
            <ChecklistItem key={id} id={id} onDeleteAndFocusPrevious={handleDelete} />
          ))}
        </SortableContext>
      </DndContext>
    </div>
  )
}

function InlineTaskList({ tasks }: { tasks: DbObject[] }) {
  const revealTask = useRevealTask()
  const handleComplete = useCallback((id: string) => { completeTask(id) }, [])
  const handleUncomplete = useCallback((id: string) => { uncompleteTask(id) }, [])
  const handleDelete = useCallback((id: string) => { deleteChecklistItem(id) }, [])
  const [showAll, setShowAll] = useState(tasks.length <= VIEW_PREVIEW_COUNT)

  // Live query: keep task data fresh from DB so status changes (complete/cancel) persist visually
  const initialIds = useMemo(() => tasks.map(t => t.id as string), [tasks])
  const [liveItems] = useLiveQueryImmediate(
    () => db.totodo.bulkGet(initialIds).then((rows) =>
      rows.filter((r): r is DbObject => r != null)
    ),
    ["chat-task-list", ...initialIds],
    undefined,
  )
  const items = liveItems ?? tasks

  const checklistItems = useMemo(() => items.filter(t => t.type != null && t.type < ObjectType.Task), [items])
  const taskItems = useMemo(() => items.filter(t => t.type == null || t.type >= ObjectType.Task), [items])
  const checklistIds = useMemo(() => checklistItems.map(t => t.id as string), [checklistItems])

  const visibleTasks = showAll ? taskItems : taskItems.slice(0, VIEW_PREVIEW_COUNT)
  const hiddenCount = taskItems.length - visibleTasks.length + (showAll ? 0 : checklistItems.length)

  return (
    <div className="mt-2 rounded-lg border border-border/40 bg-background/50 overflow-hidden" style={{ zoom: 0.85 }}>
      {visibleTasks.map((task, i) => (
        <TaskItem
          key={task.id as string}
          task={task}
          onComplete={handleComplete}
          onUncomplete={handleUncomplete}
          onOpen={revealTask}
          singleClickOpen
          isFirstInGroup={i === 0}
          isLastInGroup={!showAll ? i === visibleTasks.length - 1 : i === taskItems.length - 1 && checklistIds.length === 0}
        />
      ))}
      {showAll && checklistIds.length > 0 && (
        <DndContext>
          <SortableContext items={checklistIds} strategy={verticalListSortingStrategy}>
            {checklistItems.map((item) => (
              <ChecklistSearchRow key={item.id as string} item={item} onNavigate={revealTask} onDelete={handleDelete} />
            ))}
          </SortableContext>
        </DndContext>
      )}
      {hiddenCount > 0 && (
        <button
          onClick={() => setShowAll(true)}
          className="flex w-full items-center gap-2 px-3 py-2 text-xs text-muted-foreground hover:bg-muted/50 transition-colors"
        >
          <ChevronDownIcon className="h-3.5 w-3.5" />
          <span>Visa {hiddenCount} till</span>
        </button>
      )}
    </div>
  )
}

const PREVIEW_COUNT = 3
const VIEW_PREVIEW_COUNT = 5

// Container list rendering — routes containers through TaskItem for full interactions
function InlineContainerList({ containers }: { containers: DbObject[] }) {
  const revealTask = useRevealTask()
  const handleComplete = useCallback((id: string) => { completeTask(id) }, [])
  const handleUncomplete = useCallback((id: string) => { uncompleteTask(id) }, [])
  const [showAll, setShowAll] = useState(containers.length <= VIEW_PREVIEW_COUNT)

  // Live query: keep container data fresh from DB
  const initialIds = useMemo(() => containers.map(c => c.id as string), [containers])
  const [liveItems] = useLiveQueryImmediate(
    () => db.totodo.bulkGet(initialIds).then((rows) =>
      rows.filter((r): r is DbObject => r != null)
    ),
    ["chat-container-list", ...initialIds],
    undefined,
  )
  const items = liveItems ?? containers

  const visibleContainers = showAll ? items : items.slice(0, VIEW_PREVIEW_COUNT)
  const hiddenCount = items.length - visibleContainers.length

  return (
    <div className="mt-2 rounded-lg border border-border/40 bg-background/50 overflow-hidden" style={{ zoom: 0.85 }}>
      {visibleContainers.map((container, i) => (
        <TaskItem
          key={container.id as string}
          task={container}
          onComplete={handleComplete}
          onUncomplete={handleUncomplete}
          onOpen={revealTask}
          singleClickOpen
          isFirstInGroup={i === 0}
          isLastInGroup={i === visibleContainers.length - 1}
        />
      ))}
      {hiddenCount > 0 && (
        <button
          onClick={() => setShowAll(true)}
          className="flex w-full items-center gap-2 px-3 py-2 text-xs text-muted-foreground hover:bg-muted/50 transition-colors"
        >
          <ChevronDownIcon className="h-3.5 w-3.5" />
          <span>Visa {hiddenCount} till</span>
        </button>
      )}
    </div>
  )
}

function CollapsibleTaskList({ tasks, count }: { tasks: DbObject[]; count: number }) {
  const [showAll, setShowAll] = useState(count <= PREVIEW_COUNT)
  const revealTask = useRevealTask()
  const handleComplete = useCallback((id: string) => { completeTask(id) }, [])
  const handleUncomplete = useCallback((id: string) => { uncompleteTask(id) }, [])
  const handleDelete = useCallback((id: string) => { deleteChecklistItem(id) }, [])

  // Live query: keep task data fresh from DB so status changes (complete/cancel) persist visually
  const initialIds = useMemo(() => tasks.map(t => t.id as string), [tasks])
  const [liveItems] = useLiveQueryImmediate(
    () => db.totodo.bulkGet(initialIds).then((rows) =>
      rows.filter((r): r is DbObject => r != null)
    ),
    ["chat-search-list", ...initialIds],
    undefined,
  )
  const items = liveItems ?? tasks

  const checklistItems = useMemo(() => items.filter(t => t.type != null && t.type < ObjectType.Task), [items])
  const taskItems = useMemo(() => items.filter(t => t.type == null || t.type >= ObjectType.Task), [items])
  const checklistIds = useMemo(() => checklistItems.map(t => t.id as string), [checklistItems])

  // Show top results immediately, rest behind "show more"
  const visibleTasks = showAll ? taskItems : taskItems.slice(0, PREVIEW_COUNT)
  const hiddenCount = taskItems.length - visibleTasks.length + (showAll ? 0 : checklistItems.length)

  return (
    <div className="mt-2 rounded-lg border border-border/40 bg-background/50 overflow-hidden">
      <div style={{ zoom: 0.85 }}>
        {visibleTasks.map((task, i) => (
          <TaskItem
            key={task.id as string}
            task={task}
            onComplete={handleComplete}
            onUncomplete={handleUncomplete}
            onOpen={revealTask}
            singleClickOpen
            isFirstInGroup={i === 0}
            isLastInGroup={!showAll ? i === visibleTasks.length - 1 : i === taskItems.length - 1 && checklistIds.length === 0}
          />
        ))}
        {showAll && checklistIds.length > 0 && (
          <DndContext>
            <SortableContext items={checklistIds} strategy={verticalListSortingStrategy}>
              {checklistItems.map((item) => (
                <ChecklistSearchRow key={item.id as string} item={item} onNavigate={revealTask} onDelete={handleDelete} />
              ))}
            </SortableContext>
          </DndContext>
        )}
      </div>
      {hiddenCount > 0 && (
        <button
          onClick={() => setShowAll(true)}
          className="flex w-full items-center gap-2 px-3 py-2 text-xs text-muted-foreground hover:bg-muted/50 transition-colors"
        >
          <ChevronDownIcon className="h-3.5 w-3.5" />
          <span>Visa {hiddenCount} till</span>
        </button>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// ChecklistSearchRow — wraps ChecklistItem with a "go to parent task" icon
// ---------------------------------------------------------------------------

function ChecklistSearchRow({ item, onNavigate, onDelete }: { item: DbObject; onNavigate: (id: string) => void; onDelete: (id: string) => void }) {
  const segments = (item.parentPath ?? "").split("/")
  const parentTaskId = segments[segments.length - 1]

  const [parentTitle] = useLiveQueryImmediate(
    () => parentTaskId && !parentTaskId.startsWith("#")
      ? db.totodo.get(parentTaskId).then(t => t?.title ?? "")
      : Promise.resolve(""),
    ["cl-parent-title", parentTaskId ?? ""],
    "",
  )

  const handleGoToTask = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    if (parentTaskId && !parentTaskId.startsWith("#")) {
      onNavigate(parentTaskId)
    }
  }, [parentTaskId, onNavigate])

  return (
    <div className="group/clrow flex items-center">
      <div className="min-w-0 flex-1">
        <ChecklistItem id={item.id as string} onDeleteAndFocusPrevious={onDelete} context="standalone" compact readOnly />
      </div>
      <button
        onClick={handleGoToTask}
        className="mr-2 shrink-0 rounded p-1 text-muted-foreground/50 transition-colors hover:text-foreground"
        title={parentTitle || "Go to task"}
      >
        <GoToIcon className="size-3.5" />
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// InlineChoiceUI — renders clickable option chips for disambiguation
// ---------------------------------------------------------------------------

function InlineChoiceUI({
  choice,
  onSelect,
}: {
  choice: ChoiceData
  onSelect?: (text: string) => void
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const isMulti = choice.type === "multiple"

  const handleClick = useCallback((option: ChoiceOption) => {
    if (isMulti) {
      setSelected((prev) => {
        const next = new Set(prev)
        if (next.has(option.value)) next.delete(option.value)
        else next.add(option.value)
        return next
      })
    } else {
      // Single select — send label + value so AI can resolve it
      // Format: visible label, with value in brackets for AI context
      onSelect?.(`${option.label} [${option.value}]`)
    }
  }, [isMulti, onSelect])

  const handleSubmitMulti = useCallback(() => {
    if (selected.size === 0) return
    const selectedOptions = choice.options.filter((o) => selected.has(o.value))
    onSelect?.(selectedOptions.map((o) => `${o.label} [${o.value}]`).join(", "))
  }, [selected, choice.options, onSelect])

  return (
    <div className="mt-3 space-y-2">
      <div className="flex flex-wrap gap-2">
        {choice.options.map((option) => (
          <button
            key={option.value}
            onClick={() => handleClick(option)}
            className={cn(
              "inline-flex flex-col items-start rounded-lg border px-3 py-2 text-left text-sm transition-all",
              "hover:bg-primary/10 hover:border-primary/40",
              "active:scale-[0.97]",
              isMulti && selected.has(option.value)
                ? "border-primary bg-primary/10 ring-1 ring-primary/30"
                : "border-border/60 bg-background/80",
            )}
          >
            <span className="font-medium">{option.label}</span>
            {option.description && (
              <span className="text-xs text-muted-foreground mt-0.5">{option.description}</span>
            )}
          </button>
        ))}
      </div>
      {isMulti && selected.size > 0 && (
        <button
          onClick={handleSubmitMulti}
          className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          Bekräfta ({selected.size} valda)
        </button>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// InlineFormUI — renders a dynamic form inline
// ---------------------------------------------------------------------------

function InlineFormUI({
  form,
  onSubmit,
}: {
  form: FormData
  onSubmit?: (text: string) => void
}) {
  const [values, setValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {}
    for (const field of form.fields) {
      initial[field.key] = field.defaultValue ?? ""
    }
    return initial
  })
  const [submitted, setSubmitted] = useState(false)

  const handleChange = useCallback((key: string, value: string) => {
    setValues(prev => ({ ...prev, [key]: value }))
  }, [])

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault()
    if (submitted) return
    setSubmitted(true)
    onSubmit?.(JSON.stringify(values))
  }, [values, onSubmit, submitted])

  return (
    <form onSubmit={handleSubmit} className="mt-3 space-y-3 rounded-lg border border-border/60 p-4 bg-background/50">
      <h4 className="text-sm font-semibold">{form.title}</h4>
      {form.fields.map((field) => (
        <div key={field.key} className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">
            {field.label}{field.required && <span className="text-destructive ml-0.5">*</span>}
          </label>
          {field.type === "select" ? (
            <select
              value={values[field.key] ?? ""}
              onChange={(e) => handleChange(field.key, e.target.value)}
              disabled={submitted}
              className="w-full rounded-md border border-border/60 bg-background px-3 py-1.5 text-sm"
            >
              <option value="">{field.placeholder ?? "Välj..."}</option>
              {field.options?.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          ) : field.type === "date" ? (
            <input
              type="date"
              value={values[field.key] ?? ""}
              onChange={(e) => handleChange(field.key, e.target.value)}
              disabled={submitted}
              className="w-full rounded-md border border-border/60 bg-background px-3 py-1.5 text-sm"
            />
          ) : field.type === "number" ? (
            <input
              type="number"
              value={values[field.key] ?? ""}
              onChange={(e) => handleChange(field.key, e.target.value)}
              placeholder={field.placeholder}
              disabled={submitted}
              className="w-full rounded-md border border-border/60 bg-background px-3 py-1.5 text-sm"
            />
          ) : field.type === "toggle" ? (
            <button
              type="button"
              onClick={() => handleChange(field.key, values[field.key] === "true" ? "false" : "true")}
              disabled={submitted}
              className={cn(
                "relative inline-flex h-5 w-9 items-center rounded-full transition-colors",
                values[field.key] === "true" ? "bg-primary" : "bg-muted"
              )}
            >
              <span className={cn(
                "inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform",
                values[field.key] === "true" ? "translate-x-4.5" : "translate-x-0.5"
              )} />
            </button>
          ) : (
            <input
              type="text"
              value={values[field.key] ?? ""}
              onChange={(e) => handleChange(field.key, e.target.value)}
              placeholder={field.placeholder}
              disabled={submitted}
              className="w-full rounded-md border border-border/60 bg-background px-3 py-1.5 text-sm"
            />
          )}
        </div>
      ))}
      <button
        type="submit"
        disabled={submitted}
        className="rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
      >
        {form.submitLabel ?? "Skicka"}
      </button>
    </form>
  )
}

// ---------------------------------------------------------------------------
// InlineConfirmUI — confirmation dialog before destructive operations
// ---------------------------------------------------------------------------

function InlineConfirmUI({
  confirm,
  onSubmit,
}: {
  confirm: ConfirmData
  onSubmit?: (text: string) => void
}) {
  const [answered, setAnswered] = useState(false)

  const handleConfirm = useCallback(() => {
    if (answered) return
    setAnswered(true)
    onSubmit?.("confirmed")
  }, [onSubmit, answered])

  const handleCancel = useCallback(() => {
    if (answered) return
    setAnswered(true)
    onSubmit?.("canceled")
  }, [onSubmit, answered])

  return (
    <div className="mt-3 rounded-lg border border-border/60 p-4 space-y-3 bg-background/50">
      <h4 className="text-sm font-semibold">{confirm.title}</h4>
      <p className="text-sm text-muted-foreground">{confirm.message}</p>
      {confirm.details && confirm.details.length > 0 && (
        <ul className="text-xs text-muted-foreground space-y-0.5 pl-4 list-disc">
          {confirm.details.map((d, i) => <li key={i}>{d}</li>)}
        </ul>
      )}
      <div className="flex gap-2 pt-1">
        <button
          onClick={handleConfirm}
          disabled={answered}
          className={cn(
            "rounded-md px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50",
            confirm.variant === "destructive"
              ? "bg-destructive text-white hover:bg-destructive/90"
              : "bg-primary text-primary-foreground hover:bg-primary/90"
          )}
        >
          {confirm.confirmLabel ?? "Bekräfta"}
        </button>
        <button
          onClick={handleCancel}
          disabled={answered}
          className="rounded-md border border-border/60 px-3 py-1.5 text-sm font-medium hover:bg-muted transition-colors disabled:opacity-50"
        >
          {confirm.cancelLabel ?? "Avbryt"}
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// InlineTaskPreviewUI — shows tasks before creation for approval
// ---------------------------------------------------------------------------

function InlineTaskPreviewUI({
  preview,
  onSubmit,
}: {
  preview: TaskPreviewData
  onSubmit?: (text: string) => void
}) {
  const [answered, setAnswered] = useState(false)

  const handleApprove = useCallback(() => {
    if (answered) return
    setAnswered(true)
    onSubmit?.("approve")
  }, [onSubmit, answered])

  const handleEdit = useCallback(() => {
    if (answered) return
    setAnswered(true)
    onSubmit?.("edit")
  }, [onSubmit, answered])

  return (
    <div className="mt-3 rounded-lg border border-border/60 p-4 space-y-3 bg-background/50">
      <div className="flex items-center gap-2">
        <span className="text-base">📋</span>
        <h4 className="text-sm font-semibold">{preview.project}</h4>
      </div>
      <div className="space-y-1.5">
        {preview.tasks.map((task, i) => (
          <div key={i} className="flex items-start gap-2 text-sm">
            <span className="mt-0.5 h-4 w-4 rounded-full border border-border/80 shrink-0" />
            <div>
              <span className="font-medium">{task.title}</span>
              {task.checklist && task.checklist.length > 0 && (
                <div className="ml-2 mt-0.5 space-y-0.5">
                  {task.checklist.map((item, j) => (
                    <div key={j} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <span className="h-3 w-3 rounded-sm border border-border/60 shrink-0" />
                      {item}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-2 pt-1">
        <button
          onClick={handleApprove}
          disabled={answered}
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
        >
          Skapa
        </button>
        <button
          onClick={handleEdit}
          disabled={answered}
          className="rounded-md border border-border/60 px-3 py-1.5 text-sm font-medium hover:bg-muted disabled:opacity-50 transition-colors"
        >
          Ändra...
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// InlineDatePickerUI — date picker with quick suggestions
// ---------------------------------------------------------------------------

function InlineDatePickerUI({
  picker,
  onSubmit,
}: {
  picker: DatePickerData
  onSubmit?: (text: string) => void
}) {
  const [value, setValue] = useState("")
  const [submitted, setSubmitted] = useState(false)

  const handleSuggestion = useCallback((s: string) => {
    if (submitted) return
    setSubmitted(true)
    onSubmit?.(s)
  }, [onSubmit, submitted])

  const handleSubmitDate = useCallback(() => {
    if (submitted || !value) return
    setSubmitted(true)
    onSubmit?.(value)
  }, [value, onSubmit, submitted])

  return (
    <div className="mt-3 rounded-lg border border-border/60 p-4 space-y-3 bg-background/50">
      <label className="text-sm font-medium">{picker.label}</label>
      {picker.suggestions && picker.suggestions.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {picker.suggestions.map((s) => (
            <button
              key={s}
              onClick={() => handleSuggestion(s)}
              disabled={submitted}
              className="rounded-md border border-border/60 px-2.5 py-1 text-xs font-medium hover:bg-primary/10 hover:border-primary/40 disabled:opacity-50 transition-all"
            >
              {s}
            </button>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <input
          type={picker.mode === "datetime" ? "datetime-local" : "date"}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={submitted}
          className="rounded-md border border-border/60 bg-background px-3 py-1.5 text-sm"
        />
        <button
          onClick={handleSubmitDate}
          disabled={submitted || !value}
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
        >
          Välj
        </button>
      </div>
    </div>
  )
}

function InlineTagList({ tags }: { tags: Array<{ name: string; count: number }> }) {
  const [showAll, setShowAll] = useState(tags.length <= 20)
  const visible = showAll ? tags : tags.slice(0, 20)
  const hiddenCount = tags.length - visible.length

  return (
    <div className="mt-2 rounded-lg border border-border/40 bg-background/50 p-3" style={{ zoom: 0.85 }}>
      <div className="flex flex-wrap gap-1.5">
        {visible.map((tag) => (
          <span
            key={tag.name}
            className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary"
          >
            {tag.name.startsWith("#") ? tag.name : `#${tag.name}`}
            <span className="text-muted-foreground">({tag.count})</span>
          </span>
        ))}
      </div>
      {hiddenCount > 0 && (
        <button
          onClick={() => setShowAll(true)}
          className="mt-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          Visa {hiddenCount} till
        </button>
      )}
    </div>
  )
}
